import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
    History, Search, ChevronLeft, ChevronRight,
    Loader2, FilterX, Trash2, RotateCcw, Ban, ShieldCheck, ShieldX,
    PowerOff, Power, Inbox,
} from "lucide-react";
import { toast } from "sonner";

const PAGE_SIZE = 50;

const ACTION_META = {
    delete: { label: "Trash", Icon: Trash2, cls: "border-amber-500/40 text-amber-700 bg-amber-100/60" },
    restore: { label: "Restore", Icon: RotateCcw, cls: "border-primary/40 text-primary bg-primary/8" },
    purge: { label: "Purge", Icon: Ban, cls: "border-destructive/50 text-destructive bg-destructive/8" },
    empty_trash: { label: "Empty Trash", Icon: Ban, cls: "border-destructive/50 text-destructive bg-destructive/12" },
    bulk_delete: { label: "Bulk Trash", Icon: Trash2, cls: "border-amber-500/40 text-amber-700 bg-amber-100/60" },
    bulk_restore: { label: "Bulk Restore", Icon: RotateCcw, cls: "border-primary/40 text-primary bg-primary/8" },
    bulk_purge: { label: "Bulk Purge", Icon: Ban, cls: "border-destructive/50 text-destructive bg-destructive/8" },
    activate: { label: "Activate", Icon: Power, cls: "border-primary/40 text-primary bg-primary/8" },
    deactivate: { label: "Deactivate", Icon: PowerOff, cls: "border-muted-foreground/30 text-muted-foreground bg-muted/40" },
    bulk_activate: { label: "Bulk Activate", Icon: Power, cls: "border-primary/40 text-primary bg-primary/8" },
    bulk_deactivate: { label: "Bulk Deactivate", Icon: PowerOff, cls: "border-muted-foreground/30 text-muted-foreground bg-muted/40" },
    delete_request_approved: { label: "Request approved", Icon: ShieldCheck, cls: "border-destructive/50 text-destructive bg-destructive/8" },
    delete_request_rejected: { label: "Request rejected", Icon: ShieldX, cls: "border-muted-foreground/30 text-muted-foreground bg-muted/40" },
};

function ActionPill({ action }) {
    const meta = ACTION_META[action] || { label: action, Icon: Inbox, cls: "border-border bg-muted/40 text-muted-foreground" };
    const { Icon } = meta;
    return (
        <Badge
            variant="outline"
            className={`uppercase text-[10px] tracking-wider ${meta.cls} inline-flex items-center gap-1`}
            data-testid={`audit-action-${action}`}
        >
            <Icon className="h-3 w-3" />
            {meta.label}
        </Badge>
    );
}

function ResourcePill({ type }) {
    return (
        <Badge variant="outline" className="capitalize text-[10px] tracking-wider border-border bg-background">
            {type || "—"}
        </Badge>
    );
}

function fmtDateTime(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString(undefined, {
            year: "numeric", month: "short", day: "numeric",
            hour: "2-digit", minute: "2-digit",
        });
    } catch {
        return iso;
    }
}

export default function AdminAuditLog() {
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(0);

    const [q, setQ] = useState("");
    const [action, setAction] = useState("all");
    const [resourceType, setResourceType] = useState("all");
    const [issuedFrom, setIssuedFrom] = useState("");
    const [issuedTo, setIssuedTo] = useState("");

    const offset = page * PAGE_SIZE;

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = { limit: PAGE_SIZE, offset };
            if (q.trim()) params.q = q.trim();
            if (action !== "all") params.action = action;
            if (resourceType !== "all") params.resource_type = resourceType;
            if (issuedFrom) params.issued_from = issuedFrom;
            if (issuedTo) params.issued_to = issuedTo;
            const { data } = await api.get("/admin/audit-log", { params });
            setItems(data.items || []);
            setTotal(data.total || 0);
        } catch {
            toast.error("Could not load the audit log. Try again.");
        } finally {
            setLoading(false);
        }
    }, [offset, q, action, resourceType, issuedFrom, issuedTo]);

    useEffect(() => { load(); }, [load]);

    const onSearchSubmit = (e) => {
        e.preventDefault();
        setPage(0);
        load();
    };

    const resetFilters = () => {
        setQ("");
        setAction("all");
        setResourceType("all");
        setIssuedFrom("");
        setIssuedTo("");
        setPage(0);
    };

    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
    const showingFrom = total === 0 ? 0 : offset + 1;
    const showingTo = Math.min(offset + PAGE_SIZE, total);

    const filtersActive =
        !!q || action !== "all" || resourceType !== "all" || !!issuedFrom || !!issuedTo;

    return (
        <div
            className="max-w-7xl mx-auto px-6 py-10 pb-24 space-y-6"
            data-testid="admin-audit-log"
        >
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <h1 className="font-display text-3xl text-foreground flex items-center gap-3">
                        <History className="h-7 w-7 text-primary" />
                        Audit Log
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
                        Tamper-evident record of every destructive or restorative action — who did what,
                        when, and to which record. Append-only; export-friendly for PTIB compliance reviews.
                    </p>
                </div>
                <Badge
                    variant="outline"
                    className="border-primary/40 text-primary bg-primary/5 uppercase text-[10px] tracking-wider"
                >
                    {total} event{total === 1 ? "" : "s"} on file
                </Badge>
            </div>

            <Card className="surface-card border-border">
                <CardContent className="p-5 space-y-4">
                    <form
                        onSubmit={onSearchSubmit}
                        className="flex flex-col lg:flex-row gap-3 lg:items-end"
                    >
                        <div className="flex-1 min-w-[200px]">
                            <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold block mb-1.5">
                                Search
                            </label>
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                <Input
                                    placeholder="Actor name, email, target name or ID…"
                                    value={q}
                                    onChange={(e) => setQ(e.target.value)}
                                    className="pl-9"
                                    data-testid="audit-search-input"
                                />
                            </div>
                        </div>

                        <div className="w-full lg:w-[200px]">
                            <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold block mb-1.5">
                                Action
                            </label>
                            <Select value={action} onValueChange={(v) => { setAction(v); setPage(0); }}>
                                <SelectTrigger data-testid="audit-filter-action"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All actions</SelectItem>
                                    <SelectItem value="delete">Move to trash</SelectItem>
                                    <SelectItem value="restore">Restore</SelectItem>
                                    <SelectItem value="purge">Purge (single)</SelectItem>
                                    <SelectItem value="empty_trash">Empty trash</SelectItem>
                                    <SelectItem value="bulk_delete">Bulk trash</SelectItem>
                                    <SelectItem value="bulk_restore">Bulk restore</SelectItem>
                                    <SelectItem value="bulk_purge">Bulk purge</SelectItem>
                                    <SelectItem value="delete_request_approved">Delete request approved</SelectItem>
                                    <SelectItem value="delete_request_rejected">Delete request rejected</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="w-full lg:w-[150px]">
                            <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold block mb-1.5">
                                Resource
                            </label>
                            <Select value={resourceType} onValueChange={(v) => { setResourceType(v); setPage(0); }}>
                                <SelectTrigger data-testid="audit-filter-resource"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All types</SelectItem>
                                    <SelectItem value="user">Users</SelectItem>
                                    <SelectItem value="program">Programs</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="w-full lg:w-[155px]">
                            <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold block mb-1.5">
                                From
                            </label>
                            <Input
                                type="date"
                                value={issuedFrom}
                                onChange={(e) => { setIssuedFrom(e.target.value); setPage(0); }}
                                data-testid="audit-filter-from"
                            />
                        </div>

                        <div className="w-full lg:w-[155px]">
                            <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold block mb-1.5">
                                To
                            </label>
                            <Input
                                type="date"
                                value={issuedTo}
                                onChange={(e) => { setIssuedTo(e.target.value); setPage(0); }}
                                data-testid="audit-filter-to"
                            />
                        </div>

                        <div className="flex gap-2">
                            <Button type="submit" data-testid="audit-search-submit">
                                <Search className="h-4 w-4 mr-1.5" /> Apply
                            </Button>
                            {filtersActive && (
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={resetFilters}
                                    data-testid="audit-filters-reset"
                                >
                                    <FilterX className="h-4 w-4 mr-1.5" /> Reset
                                </Button>
                            )}
                        </div>
                    </form>
                </CardContent>
            </Card>

            <Card className="surface-card border-border">
                <CardContent className="p-0">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead className="w-[180px]">When</TableHead>
                                <TableHead className="w-[160px]">Action</TableHead>
                                <TableHead className="w-[110px]">Resource</TableHead>
                                <TableHead>Target</TableHead>
                                <TableHead>Actor</TableHead>
                                <TableHead className="w-[80px] text-right">Count</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={6} className="text-center py-12 text-muted-foreground">
                                        <Loader2 className="h-5 w-5 animate-spin inline-block mr-2" />
                                        Loading…
                                    </TableCell>
                                </TableRow>
                            ) : items.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={6} className="text-center py-12 text-muted-foreground">
                                        No events match these filters yet.
                                    </TableCell>
                                </TableRow>
                            ) : (
                                items.map((row) => (
                                    <TableRow key={row.id} data-testid={`audit-row-${row.id}`}>
                                        <TableCell className="text-xs text-muted-foreground" title={row.at}>
                                            {fmtDateTime(row.at)}
                                        </TableCell>
                                        <TableCell><ActionPill action={row.action} /></TableCell>
                                        <TableCell><ResourcePill type={row.resource_type} /></TableCell>
                                        <TableCell>
                                            <div className="text-sm font-medium">
                                                {row.target_label || row.target_id || "—"}
                                            </div>
                                            {row.reason && (
                                                <div className="text-[11px] text-muted-foreground italic mt-0.5 max-w-[420px] line-clamp-2">
                                                    "{row.reason}"
                                                </div>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <div className="text-sm">{row.actor_name || "—"}</div>
                                            <div className="text-[11px] text-muted-foreground">
                                                {row.actor_email}
                                                {row.actor_role && (
                                                    <span className="ml-1.5 uppercase tracking-wider text-[9px]">
                                                        · {row.actor_role.replace("_", " ")}
                                                    </span>
                                                )}
                                            </div>
                                        </TableCell>
                                        <TableCell className="text-right font-mono text-xs">
                                            {row.count ?? "—"}
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>

                    <div className="flex items-center justify-between p-4 border-t border-border">
                        <div className="text-xs text-muted-foreground">
                            {total === 0
                                ? "0 events"
                                : `Showing ${showingFrom}–${showingTo} of ${total}`}
                        </div>
                        <div className="flex items-center gap-2">
                            <Button
                                size="sm"
                                variant="outline"
                                disabled={page === 0 || loading}
                                onClick={() => setPage((p) => Math.max(0, p - 1))}
                                data-testid="audit-page-prev"
                            >
                                <ChevronLeft className="h-4 w-4" />
                            </Button>
                            <span className="text-xs text-muted-foreground">
                                Page {page + 1} / {totalPages}
                            </span>
                            <Button
                                size="sm"
                                variant="outline"
                                disabled={page + 1 >= totalPages || loading}
                                onClick={() => setPage((p) => p + 1)}
                                data-testid="audit-page-next"
                            >
                                <ChevronRight className="h-4 w-4" />
                            </Button>
                        </div>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
